import { palette } from '@themes'
import classNames from 'classnames'
import moment from 'moment'
import { forwardRef, ReactNode, useMemo } from 'react'
import { Calendar, momentLocalizer, Views } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { FaChevronLeft, FaChevronRight, FaInfo } from 'react-icons/fa'
import './ScheduleCalendarRBC.css'

const localizer = momentLocalizer(moment)
const DAY_START = 0
const DAY_END = 23

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RBCDoctor = {
  id: string
  name: string
  patientCount: number
  initials: string
  avatarUrl?: string
}

export type RBCAppointment = {
  id: string
  start: Date
  end: Date
  resourceId: string
  title: string
  meta?: {
    patientId?: string
    patientName?: string
    type?: string
    status?: 'completed' | 'pending' | 'upcoming' | 'cancelled'
    color?: string
  }
}

export type ScheduleCalendarRBCHeaderProps = {
  date: Date
  onDateChange?: (date: Date) => void
  onDateNext: () => void
  onDatePrev: () => void
  headerRight?: ReactNode
}

export type ScheduleCalendarRBCProps = {
  header: ScheduleCalendarRBCHeaderProps
  doctors: RBCDoctor[]
  events: RBCAppointment[]
  timeZoneLabel?: string
  onEventClick?: (event: RBCAppointment) => void
  onSlotClick?: (slot: Date, resourceId: string) => void
  /** Custom header renderer; if provided, header props are not used for default UI */
  renderHeader?: (props: ScheduleCalendarRBCHeaderProps) => ReactNode
}

// ---------------------------------------------------------------------------
// Default header (for use by consumers that want the standard header UI)
// ---------------------------------------------------------------------------

const DateLabelButton = forwardRef<HTMLButtonElement, { value?: string; onClick?: () => void; children?: ReactNode }>(
  ({ children, onClick }, ref) => (
    <button
      aria-label="Choose date"
      className="cursor-pointer rounded px-1.5 py-0.5 text-base font-medium text-gray-900 hover:bg-gray-100 hover:underline"
      onClick={onClick}
      ref={ref}
      type="button"
    >
      {children}
    </button>
  )
)

DateLabelButton.displayName = 'DateLabelButton'

export function ScheduleCalendarRBCDefaultHeader(header: ScheduleCalendarRBCHeaderProps) {
  const dateLabel = useMemo(() => {
    const d = moment(header.date)
    return d.isSame(moment(), 'day') ? `Today, ${d.format('DD MMM YYYY')}` : d.format('ddd, DD MMM YYYY')
  }, [header.date])

  const handleDateChange = (d: Date | null) => {
    if (d) header.onDateChange?.(d)
  }

  return (
    <header className="schedule-calendar-rbc__header flex flex-shrink-0 items-center justify-between gap-4 border-b border-gray-200 px-4 py-3">
      <div className="flex items-center gap-2">
        <button
          aria-label="Previous day"
          className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-transparent p-1.5 text-sm hover:bg-gray-100"
          onClick={header.onDatePrev}
          type="button"
        >
          <FaChevronLeft />
        </button>
        {header.onDateChange != null ? (
          <ReactDatePicker
            customInput={<DateLabelButton>{dateLabel}</DateLabelButton>}
            dateFormat="dd MMM yyyy"
            onChange={handleDateChange}
            popperClassName="schedule-calendar-rbc__date-picker-popper"
            selected={header.date}
            showMonthDropdown
            showYearDropdown
          />
        ) : (
          <span className="text-base font-medium text-gray-900">{dateLabel}</span>
        )}
        <button
          aria-label="Next day"
          className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-transparent p-1.5 text-sm hover:bg-gray-100"
          onClick={header.onDateNext}
          type="button"
        >
          <FaChevronRight />
        </button>
      </div>
      <div className="flex flex-1 items-center justify-end gap-4">
        {header.headerRight != null && header.headerRight}
      </div>
    </header>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ScheduleCalendarRBC({
  doctors,
  events,
  header,
  onEventClick,
  onSlotClick,
  renderHeader,
  timeZoneLabel = 'GMT+01'
}: ScheduleCalendarRBCProps) {
  const components = useMemo(
    () => ({
      timeGutterHeader: () => (
        <div className="font-medium px-2 py-2 text-center text-xs text-gray-600">{timeZoneLabel}</div>
      ),
      resourceHeader: ({ resource }: { resource?: RBCDoctor }) => {
        if (!resource) return null
        return (
          <div className="schedule-calendar-rbc__doctor-header flex min-w-0 flex-1 items-center gap-2.5 px-2.5 py-2">
            {resource.avatarUrl ? (
              <img alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" src={resource.avatarUrl} />
            ) : (
              <div className="flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-medium text-rose-700 bg-rose-200">
                {resource.initials}
              </div>
            )}
            <div className="min-w-0 flex-1 flex flex-col justify-start">
              <p className="truncate text-sm font-medium text-gray-900">{resource.name}</p>
              <p className="text-xs text-gray-500">
                {resource.patientCount} patient{resource.patientCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        )
      },
      event: (props: { event: RBCAppointment }) => {
        const e = props.event
        const meta = e.meta ?? {}
        const status = meta.status
        return (
          <div
            className={classNames(
              'relative flex cursor-pointer flex-col flex-1 gap-1.5 overflow-hidden rounded-lg px-2 py-1.5 transition-opacity hover:opacity-95 m-1',
              {
                'bg-[#F7EDFF]': status === 'completed',
                'bg-yellow-50': status === 'pending',
                'bg-[#FFFBED]': status === 'upcoming',
                'bg-[#FFC8B8]': status === 'cancelled'
              }
            )}
            onClick={ev => {
              ev.stopPropagation()
              onEventClick?.(e)
            }}
            onKeyDown={ev => {
              if (ev.key === 'Enter' || ev.key === ' ') {
                ev.preventDefault()
                onEventClick?.(e)
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span className="absolute right-2 top-1.5 flex items-center justify-center">
              {status === 'completed' ? (
                <svg fill="none" height="18" viewBox="0 0 18 18" width="18" xmlns="http://www.w3.org/2000/svg">
                  <rect fill="white" height="18" rx="9" width="18" />
                  <path
                    clipRule="evenodd"
                    d="M8.99961 15.4016C10.697 15.4016 12.3249 14.7273 13.5251 13.527C14.7253 12.3268 15.3996 10.6989 15.3996 9.00156C15.3996 7.30418 14.7253 5.67631 13.5251 4.47608C12.3249 3.27585 10.697 2.60156 8.99961 2.60156C7.30222 2.60156 5.67436 3.27585 4.47413 4.47608C3.27389 5.67631 2.59961 7.30418 2.59961 9.00156C2.59961 10.6989 3.27389 12.3268 4.47413 13.527C5.67436 14.7273 7.30222 15.4016 8.99961 15.4016ZM11.9652 7.96716C12.1109 7.81628 12.1916 7.6142 12.1897 7.40444C12.1879 7.19468 12.1038 6.99403 11.9555 6.84571C11.8071 6.69738 11.6065 6.61325 11.3967 6.61142C11.187 6.6096 10.9849 6.69024 10.834 6.83596L8.19961 9.47036L7.16521 8.43596C7.01433 8.29024 6.81225 8.2096 6.60249 8.21142C6.39273 8.21325 6.19208 8.29738 6.04375 8.44571C5.89543 8.59403 5.81129 8.79468 5.80947 9.00444C5.80765 9.2142 5.88828 9.41628 6.03401 9.56716L7.63401 11.1672C7.78403 11.3171 7.98748 11.4014 8.19961 11.4014C8.41174 11.4014 8.61519 11.3171 8.76521 11.1672L11.9652 7.96716Z"
                    fill="#009A49"
                    fillRule="evenodd"
                  />
                </svg>
              ) : status === 'pending' ? (
                <FaInfo color={palette.yellowOchre} size={12} />
              ) : status === 'upcoming' ? (
                <svg fill="none" height="14" viewBox="0 0 14 14" width="14" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M2.91634 12.8346C2.75107 12.8346 2.61262 12.7786 2.50101 12.6666C2.3894 12.5546 2.3334 12.4162 2.33301 12.2513C2.33262 12.0864 2.38862 11.948 2.50101 11.836C2.6134 11.724 2.75184 11.668 2.91634 11.668H3.49968V9.91797C3.49968 9.32491 3.63832 8.76822 3.91559 8.24789C4.19287 7.72755 4.57923 7.31202 5.07468 7.0013C4.57884 6.69019 4.19248 6.27466 3.91559 5.75472C3.6387 5.23477 3.50007 4.67808 3.49968 4.08464V2.33464H2.91634C2.75107 2.33464 2.61262 2.27864 2.50101 2.16664C2.3894 2.05464 2.3334 1.91619 2.33301 1.7513C2.33262 1.58641 2.38862 1.44797 2.50101 1.33597C2.6134 1.22397 2.75184 1.16797 2.91634 1.16797H11.083C11.2483 1.16797 11.3869 1.22397 11.4989 1.33597C11.6109 1.44797 11.6667 1.58641 11.6663 1.7513C11.666 1.91619 11.61 2.05483 11.4983 2.16722C11.3867 2.27961 11.2483 2.33541 11.083 2.33464H10.4997V4.08464C10.4997 4.67769 10.3612 5.23439 10.0843 5.75472C9.80745 6.27505 9.4209 6.69058 8.92468 7.0013C9.42051 7.31241 9.80707 7.72814 10.0843 8.24847C10.3616 8.7688 10.5001 9.3253 10.4997 9.91797V11.668H11.083C11.2483 11.668 11.3869 11.724 11.4989 11.836C11.6109 11.948 11.6667 12.0864 11.6663 12.2513C11.666 12.4162 11.61 12.5548 11.4983 12.6672C11.3867 12.7796 11.2483 12.8354 11.083 12.8346H2.91634Z"
                    fill="#EC9E19"
                  />
                </svg>
              ) : null}
            </span>
            <span
              className={classNames('pr-5 text-sm font-semibold text-gray-900 capitalize', {
                'text-[#44007A]': status === 'completed',
                'text-[#555]': status === 'pending',
                'text-[#714B09]': status === 'upcoming',
                'text-rose-700': status === 'cancelled'
              })}
            >
              {meta.patientName ?? e.title}
            </span>
            <span className="text-xs text-gray-600">
              {moment(e.start).format('h:mm a')} - {moment(e.end).format('h:mm a')}
            </span>
            <span className="text-xs text-gray-600 bg-white px-1.5 py-1 rounded-xl self-start">{e.title}</span>
          </div>
        )
      },
      timeSlotWrapper: (props: { value?: Date; resource?: RBCDoctor; children?: ReactNode }) => (
        <div className="schedule-calendar-rbc__time-slot min-h-[60px] border-b border-gray-100">{props.children}</div>
      )
    }),
    [onEventClick, timeZoneLabel]
  )

  return (
    <div className="schedule-calendar-rbc flex h-full flex-col">
      {renderHeader != null && renderHeader(header)}

      <div className="schedule-calendar-rbc__scroll relative min-h-0 flex-1 overflow-auto">
        <div className="schedule-calendar-rbc__inner h-full min-h-[600px] min-w-min">
          {/* @ts-expect-error react-big-calendar Calendar types */}
          <Calendar
            components={components}
            date={header.date}
            endAccessor="end"
            eventPropGetter={(event: RBCAppointment) => ({
              className: `schedule-calendar-rbc__event-wrap`
            })}
            events={events}
            formats={{ timeGutterFormat: (date: Date) => moment(date).format('h:mm a') }}
            localizer={localizer}
            // max={}
            // min={}
            onSelectSlot={slotInfo => {
              if (slotInfo?.start != null && slotInfo?.resourceId != null) {
                onSlotClick?.(slotInfo.start, String(slotInfo.resourceId))
              }
            }}
            resourceIdAccessor="id"
            resourceTitleAccessor="name"
            resources={doctors}
            selectable
            startAccessor="start"
            step={60}
            style={{ height: '100%' }}
            timeslots={1}
            toolbar={false}
            view={Views.DAY}
            views={[Views.DAY]}
          />
        </div>
      </div>
    </div>
  )
}
